import { EnhancedSensorEntry } from 'types'

export interface SensorThresholdData {
  sensorId: string
  sensorName: string
  enabled: boolean
  lowerLowerThreshold: number | null
  lowerThreshold: number | null
  upperThreshold: number | null
  upperUpperThreshold: number | null
}

export interface ThresholdLineData {
  sensorId: string
  sensorName: string
  lowerLowerThreshold?: number
  lowerThreshold?: number
  upperThreshold?: number
  upperUpperThreshold?: number
  lowerLowerKey?: string
  lowerKey?: string
  upperKey?: string
  upperUpperKey?: string
}

/**
 * Extracts threshold configuration from a sensor entry
 */
export const extractThresholdFromSensor = (
  sensor: EnhancedSensorEntry
): SensorThresholdData => {
  const sensorData = sensor.data
  const thresholds = sensorData.thresholds
  const sensorName = sensorData?.name || sensor.id

  const result = {
    sensorId: sensor.id,
    sensorName,
    enabled: thresholds?.enabled ?? false,
    lowerLowerThreshold: thresholds?.lowerLowerThreshold ?? null,
    lowerThreshold: thresholds?.lowerThreshold ?? null,
    upperThreshold: thresholds?.upperThreshold ?? null,
    upperUpperThreshold: thresholds?.upperUpperThreshold ?? null,
  }

  return result
}

/**
 * Gets all threshold configurations from a list of sensors
 */
export const getThresholdConfigurations = (
  sensors: EnhancedSensorEntry[]
): SensorThresholdData[] => {
  return sensors.map(extractThresholdFromSensor)
}

/**
 * Gets enabled threshold configurations that have valid values
 */
export const getEnabledThresholds = (
  sensors: EnhancedSensorEntry[]
): ThresholdLineData[] => {
  return sensors
    .map(extractThresholdFromSensor)
    .filter((config) => {
      if (!config.enabled) return false

      // At least one threshold must be configured
      const hasAnyThreshold = [
        config.lowerLowerThreshold,
        config.lowerThreshold,
        config.upperThreshold,
        config.upperUpperThreshold,
      ].some((threshold) => threshold !== null)

      if (!hasAnyThreshold) return false

      // Validate threshold ordering if multiple thresholds are present
      const thresholds = [
        config.lowerLowerThreshold,
        config.lowerThreshold,
        config.upperThreshold,
        config.upperUpperThreshold,
      ].filter((t) => t !== null) as number[]

      // Check if thresholds are in ascending order
      for (let i = 0; i < thresholds.length - 1; i++) {
        if (thresholds[i] >= thresholds[i + 1]) {
          return false
        }
      }

      return true
    })
    .map((config) => {
      const result: ThresholdLineData = {
        sensorId: config.sensorId,
        sensorName: config.sensorName,
      }

      if (config.lowerLowerThreshold !== null) {
        result.lowerLowerThreshold = config.lowerLowerThreshold
        result.lowerLowerKey = `${config.sensorName}_lowerLowerThreshold`
      }

      if (config.lowerThreshold !== null) {
        result.lowerThreshold = config.lowerThreshold
        result.lowerKey = `${config.sensorName}_lowerThreshold`
      }

      if (config.upperThreshold !== null) {
        result.upperThreshold = config.upperThreshold
        result.upperKey = `${config.sensorName}_upperThreshold`
      }

      if (config.upperUpperThreshold !== null) {
        result.upperUpperThreshold = config.upperUpperThreshold
        result.upperUpperKey = `${config.sensorName}_upperUpperThreshold`
      }

      return result
    })
}

/**
 * Adds threshold data to chart data points for enabled sensors
 */
export const addThresholdDataToChartPoints = (
  chartDataPoints: Record<string, Record<string, number | string | null>>,
  enabledThresholds: ThresholdLineData[]
): void => {
  enabledThresholds.forEach((threshold) => {
    Object.values(chartDataPoints).forEach(
      (dataPoint: Record<string, number | string | null>) => {
        if (
          threshold.lowerLowerKey &&
          threshold.lowerLowerThreshold !== undefined
        ) {
          dataPoint[threshold.lowerLowerKey] = threshold.lowerLowerThreshold
        }
        if (threshold.lowerKey && threshold.lowerThreshold !== undefined) {
          dataPoint[threshold.lowerKey] = threshold.lowerThreshold
        }
        if (threshold.upperKey && threshold.upperThreshold !== undefined) {
          dataPoint[threshold.upperKey] = threshold.upperThreshold
        }
        if (
          threshold.upperUpperKey &&
          threshold.upperUpperThreshold !== undefined
        ) {
          dataPoint[threshold.upperUpperKey] = threshold.upperUpperThreshold
        }
      }
    )
  })
}

/**
 * Gets unique threshold values for reference lines (in case multiple sensors have the same threshold values)
 */
export const getUniqueThresholdValues = (
  enabledThresholds: ThresholdLineData[]
): Array<{
  value: number
  type: 'lowerLower' | 'lower' | 'upper' | 'upperUpper'
  sensors: string[]
}> => {
  const thresholdMap = new Map<
    number,
    { type: 'lowerLower' | 'lower' | 'upper' | 'upperUpper'; sensors: string[] }
  >()

  const addThreshold = (
    value: number | undefined,
    type: 'lowerLower' | 'lower' | 'upper' | 'upperUpper',
    sensorName: string
  ) => {
    if (value === undefined) return

    if (thresholdMap.has(value)) {
      const existing = thresholdMap.get(value)!
      if (existing.type === type) {
        existing.sensors.push(sensorName)
      }
    } else {
      thresholdMap.set(value, {
        type,
        sensors: [sensorName],
      })
    }
  }

  enabledThresholds.forEach((threshold) => {
    addThreshold(
      threshold.lowerLowerThreshold,
      'lowerLower',
      threshold.sensorName
    )
    addThreshold(threshold.lowerThreshold, 'lower', threshold.sensorName)
    addThreshold(threshold.upperThreshold, 'upper', threshold.sensorName)
    addThreshold(
      threshold.upperUpperThreshold,
      'upperUpper',
      threshold.sensorName
    )
  })

  return Array.from(thresholdMap.entries()).map(([value, data]) => ({
    value,
    ...data,
  }))
}

/**
 * Validates threshold configuration
 */
export const validateThresholdConfig = (
  config: SensorThresholdData
): string | null => {
  if (!config.enabled) return null

  // At least one threshold must be configured
  const configuredThresholds = [
    config.lowerLowerThreshold,
    config.lowerThreshold,
    config.upperThreshold,
    config.upperUpperThreshold,
  ].filter((threshold) => threshold !== null)

  if (configuredThresholds.length === 0) {
    return 'At least one threshold must be configured when enabled'
  }

  // Validate threshold ordering
  const thresholdPairs = [
    { value: config.lowerLowerThreshold, name: 'Lower Low' },
    { value: config.lowerThreshold, name: 'Lower' },
    { value: config.upperThreshold, name: 'Upper' },
    { value: config.upperUpperThreshold, name: 'Upper High' },
  ].filter((pair) => pair.value !== null)

  for (let i = 0; i < thresholdPairs.length - 1; i++) {
    if (thresholdPairs[i].value! >= thresholdPairs[i + 1].value!) {
      return `${thresholdPairs[i].name} threshold (${thresholdPairs[i].value}) must be less than ${thresholdPairs[i + 1].name} threshold (${thresholdPairs[i + 1].value})`
    }
  }

  return null
}

/**
 * Gets threshold statistics for display
 */
export const getThresholdStatistics = (
  sensors: EnhancedSensorEntry[]
): {
  total: number
  enabled: number
  configured: number
} => {
  const configurations = getThresholdConfigurations(sensors)

  return {
    total: configurations.length,
    enabled: configurations.filter((c) => c.enabled).length,
    configured: configurations.filter((c) =>
      [
        c.lowerLowerThreshold,
        c.lowerThreshold,
        c.upperThreshold,
        c.upperUpperThreshold,
      ].some((threshold) => threshold !== null)
    ).length,
  }
}
